import { useToast } from "@themis/ui";
import React from "react";
import {
  generatePath,
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom";

import { useAccount } from "@/api/queries/accounts";
import {
  useContract,
  useCreateContract,
  useUpdateContract,
} from "@/api/queries/contracts";
import { Header } from "@/components/Layout/Header";
import { PageContent } from "@/components/Layout/PageContent";
import { PageLayout } from "@/components/Layout/PageLayout";
import LoadingWrapper from "@/components/loading-wrapper";

import { ACCOUNT_ID_PARAM, ACCOUNT_ROUTES, CONTRACT_ID_PARAM } from ".";
import { ActiveTag } from "../components/ActiveTag";
import {
  ContractInfoForm,
  ContractInfoSchema,
} from "../components/Contracts/ContractInfoForm";
import { Subtitle } from "../components/Subtitle";
import { isContractActive } from "../utils";

function StatusContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="tw-box-border tw-flex tw-h-8 tw-items-center tw-justify-center tw-gap-2 tw-rounded-md tw-bg-neutral-50 tw-px-2.5 tw-py-1.5">
      {children}
    </div>
  );
}

export function ContractDetails() {
  const history = useHistory();
  const toast = useToast();

  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const redirectTo = searchParams.get("redirectTo");

  const { workspace_id, accountId, contractId } = useParams<{
    workspace_id: string;
    [ACCOUNT_ID_PARAM]: string;
    [CONTRACT_ID_PARAM]: string;
  }>();

  const {
    data: accountData,
    isLoading: isAccountLoading,
    error: accountError,
  } = useAccount(Number(workspace_id), Number(accountId));

  const {
    data: contractData,
    isLoading: isContractLoading,
    error: contractError,
  } = useContract(Number(contractId));

  const contractsPath = generatePath(
    `/workspaces/:workspace_id/accounts${ACCOUNT_ROUTES.contractsList}`,
    {
      accountId,
      workspace_id,
    },
  );

  const { mutateAsync: createContract } = useCreateContract({
    accountId: Number(accountId),
    onSuccess: () => {
      toast({ content: "Contract has been created!", variant: "success" });
      history.replace(contractsPath);
    },
    onError: () => {
      toast({
        content: "Something went wrong. Could not create contract.",
        variant: "error",
      });
    },
  });

  const { mutateAsync: updateContract } = useUpdateContract({
    contractId: Number(contractId),
    onSuccess: () => {
      toast({ content: "Contract saved successfully!", variant: "success" });
      history.replace(redirectTo ? redirectTo : contractsPath);
    },
    onError: () => {
      toast({
        content: "Something went wrong. Could not save account.",
        variant: "error",
      });
    },
  });

  const handleSubmitForm = async (values: ContractInfoSchema) => {
    const requestBody = { contract: { ...values, file: values.file.signedId } };

    if (!contractData) {
      await createContract(requestBody);
      return;
    }

    await updateContract(requestBody);
  };

  return (
    <PageLayout>
      <Header
        onClickBackButton={history.goBack}
        title={
          <div className="tw-align-center tw-flex tw-gap-1">
            Accounts
            {!(isAccountLoading || accountError) && (
              <Subtitle>{accountData?.account.name}</Subtitle>
            )}
            {!(isContractLoading || contractError) && (
              <Subtitle>{contractData?.contract?.file.name}</Subtitle>
            )}
          </div>
        }
      />
      <PageContent>
        <div className="tw-flex tw-min-w-96 tw-flex-col tw-gap-8 tw-px-32">
          <LoadingWrapper
            loadingLayout="small-table"
            loading={isContractLoading}
            errorText={contractError && "Could not load contract details."}
          >
            <div className="tw-flex tw-flex-row tw-gap-2">
              <StatusContainer>
                <label className="tw-text-sm tw-font-semibold tw-text-neutral-500">
                  Status
                </label>
                <ActiveTag
                  isActive={
                    contractData?.contract?.end_date
                      ? isContractActive(contractData?.contract?.end_date)
                      : true
                  }
                />
              </StatusContainer>
            </div>
            <div className="tw-flex tw-flex-col tw-gap-2">
              <h2 className="tw-text-lg tw-font-semibold tw-text-neutral-500">
                Contract Info
              </h2>
              <ContractInfoForm
                onSubmit={handleSubmitForm}
                contract={contractData?.contract}
              />
            </div>
          </LoadingWrapper>
        </div>
      </PageContent>
    </PageLayout>
  );
}
